import {
  Injectable,
  OnDestroy,
  inject,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { NzModalService, NzModalRef } from 'ng-zorro-antd/modal';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth/auth.service';
import { AuthTeardownService } from './auth/auth-teardown.service';
import { UserStore } from '../app-state/user.store';
import { TabActivityService } from './tab-activity.service';
import { AudioActivityService } from './audio-activity.service';
import { environment } from '../../environments/environment';

const MINUTE_MS = 60 * 1000;
const DEFAULT_INACTIVITY_MINUTES = 15;
// Upper bound keeps a typo or an unreviewed edit from silently disabling
// automatic logoff, which is a HIPAA safeguard on shared clinical workstations.
const MAX_INACTIVITY_MINUTES = 60;

export function resolveInactivityThresholdMs(minutes: unknown): number {
  const isValid =
    typeof minutes === 'number' &&
    Number.isFinite(minutes) &&
    minutes > 0 &&
    minutes <= MAX_INACTIVITY_MINUTES;
  return (isValid ? minutes : DEFAULT_INACTIVITY_MINUTES) * MINUTE_MS;
}

@Injectable({ providedIn: 'root' })
export class InactivityService implements OnDestroy {
  private authService = inject(AuthService);
  private userStore = inject(UserStore);
  private readonly authTeardown = inject(AuthTeardownService);
  private modal = inject(NzModalService);
  private tabActivityService = inject(TabActivityService);
  private audioActivityService = inject(AudioActivityService);
  private router = inject(Router);

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  private readonly inactivityThresholdMs = resolveInactivityThresholdMs(
    environment.inactivityTimeoutMinutes,
  );
  private readonly warningCountdownMs = 2 * 60 * 1000; // 2 minutes warning countdown
  private readonly heartbeatIntervalMs = 15_000; // 15s tick (reasonable for production)
  private readonly tokenRefreshInterval = 10 * 60 * 1000; // Refresh token every 10 minutes

  private intervalId: ReturnType<typeof setInterval> | null = null;
  private warningModal: NzModalRef | null = null;
  /** Wall-clock end of the current warning window */
  private warningEndsAt = 0;
  private isRefreshingToken = false;
  private activityHandler: EventListener | null = null;

  start(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (this.intervalId) {
      return;
    }
    this.bindActivityListeners();
    this.tabActivityService.updateLastActivity();
    // Initialize token refresh time to start the auto-refresh cycle immediately
    this.tabActivityService.updateTokenRefreshTime(Date.now());
    this.tabActivityService.claimOrRenewLeadership();
    this.intervalId = setInterval(() => this.tick(), this.heartbeatIntervalMs);

    // Set up cross-tab synchronization callbacks
    this.tabActivityService.setCallback('logout', () =>
      this.handleLogoutBroadcast(),
    );
    this.tabActivityService.setCallback('continue_session', () =>
      this.handleContinueSessionBroadcast(),
    );
    this.tabActivityService.setCallback(
      'warning_state',
      (isActive: boolean, endsAt: number) =>
        this.handleWarningStateBroadcast(isActive, endsAt),
    );

    // Wipe any leftover warning so a new login never reopens a stale modal
    // or forceLogout from a previous session's endsAt.
    this.tabActivityService.broadcastWarningState(false, 0);
  }

  // Method to restart the service when token becomes available
  restartIfTokenAvailable(): void {
    const token =
      localStorage.getItem('token') || localStorage.getItem('access_token');
    if (token && !this.intervalId) {
      this.start();
    }
  }

  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.unbindActivityListeners();
    // Don't re-broadcast when stopping (logout / follower sync already handled)
    this.closeWarning(false);
    this.tabActivityService.releaseLeadershipIfHeld();
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private bindActivityListeners(): void {
    this.activityHandler = () => {
      this.tabActivityService.updateLastActivity();
    };
    window.addEventListener('mousemove', this.activityHandler, {
      passive: true,
    });
    window.addEventListener('mousedown', this.activityHandler, {
      passive: true,
    });
    window.addEventListener('keydown', this.activityHandler);
    window.addEventListener('scroll', this.activityHandler, { passive: true });
    window.addEventListener('touchstart', this.activityHandler, {
      passive: true,
    });
  }

  private unbindActivityListeners(): void {
    if (!this.activityHandler) return;
    window.removeEventListener('mousemove', this.activityHandler);
    window.removeEventListener('mousedown', this.activityHandler);
    window.removeEventListener('keydown', this.activityHandler);
    window.removeEventListener('scroll', this.activityHandler);
    window.removeEventListener('touchstart', this.activityHandler);
    this.activityHandler = null;
  }

  private tick(): void {
    // Patient questionnaire is a public, standalone page — no app session or expiry
    if (this.router.url.includes('patient-questionnaire')) {
      return;
    }

    // Stop monitoring if user is no longer logged in
    const token =
      localStorage.getItem('token') || localStorage.getItem('access_token');
    if (!token) {
      this.stop();
      return;
    }

    // If audio capture/scribe is active, treat as activity to keep session alive
    if (this.audioActivityService.isActive()) {
      this.tabActivityService.updateLastActivity();
    }

    const lastActivity = this.tabActivityService.getLastActivity();
    const timeSinceLastActivity = Date.now() - lastActivity;
    const lastTokenRefresh = this.tabActivityService.getLastTokenRefresh();
    const timeSinceLastRefresh = Date.now() - lastTokenRefresh;

    const isLeader = this.tabActivityService.claimOrRenewLeadership();

    // Only the leader starts proactive refresh; navigator.locks serializes the call
    if (isLeader && timeSinceLastRefresh >= this.tokenRefreshInterval) {
      this.refreshToken();
    }

    // Active warning only; stale endsAt from a prior session is cleared on start
    const warning = this.tabActivityService.getWarningStateIfActive();

    if (warning) {
      this.warningEndsAt = warning.endsAt;
      if (Date.now() >= this.warningEndsAt) {
        this.forceLogout();
        return;
      }
      if (!this.warningModal) {
        this.openWarning(false);
      }
      return;
    }

    // Leader opens the inactivity warning after the idle threshold
    if (
      isLeader &&
      timeSinceLastActivity >= this.inactivityThresholdMs &&
      !this.warningModal
    ) {
      this.warningEndsAt = Date.now() + this.warningCountdownMs;
      this.openWarning(true);
    }
  }

  /**
   * @param broadcast When true (originator), publish warning state to other tabs.
   *                  Followers pass false so they only show the local modal.
   */
  private openWarning(broadcast: boolean): void {
    if (this.warningModal) {
      return;
    }

    this.warningModal = this.modal.confirm({
      nzTitle: 'Session about to expire',
      nzContent: `You have been inactive. Do you want to continue your session?`,
      nzOkText: 'Continue session',
      nzCancelText: 'Logout',
      nzClosable: false,
      nzMaskClosable: false,
      nzOnOk: () => this.refreshSession(),
      nzOnCancel: () => this.forceLogout(),
    });

    if (broadcast) {
      this.tabActivityService.broadcastWarningState(true, this.warningEndsAt);
    }
  }

  private closeWarning(broadcast = true): void {
    if (this.warningModal) {
      this.warningModal.close();
      this.warningModal = null;
    }

    this.warningEndsAt = 0;

    if (broadcast) {
      this.tabActivityService.broadcastWarningState(false, 0);
    }
  }

  private refreshToken(): void {
    void this.performTokenRefresh(false);
  }

  private refreshSession(): void {
    void this.performTokenRefresh(true);
  }

  private async performTokenRefresh(showNotification: boolean): Promise<void> {
    await this.tabActivityService.runWithTokenRefreshLock(async () => {
      // Another tab may have refreshed while we waited for the lock
      if (!showNotification) {
        const timeSinceLastRefresh =
          Date.now() - this.tabActivityService.getLastTokenRefresh();
        if (timeSinceLastRefresh < this.tokenRefreshInterval) {
          return;
        }
      }

      if (this.isRefreshingToken) {
        return;
      }
      this.isRefreshingToken = true;

      // Write timestamp BEFORE the request so a new leader mid-flight
      // does not see a stale "due" and fire a second refresh.
      this.tabActivityService.updateTokenRefreshTime(Date.now());

      try {
        const response = await firstValueFrom(
          this.authService.refreshStandalone(),
        );
        const token = response?.data?.token;
        if (token) {
          localStorage.setItem('token', token);
          this.tabActivityService.updateTokenRefreshTime(Date.now());
          this.userStore.setToken(token);
        }

        if (showNotification) {
          this.tabActivityService.updateLastActivity();
          this.tabActivityService.continueSession();
          this.closeWarning(true);
        }
      } catch {
        this.forceLogout();
      } finally {
        this.isRefreshingToken = false;
      }
    });
  }

  private forceLogout(): void {
    this.closeWarning(true);
    this.stop();
    this.tabActivityService.releaseLeadershipIfHeld();
    this.tabActivityService.logout();
    // Clear local state and navigate using existing interceptor/guard behavior
    this.authTeardown.clearAuthentication({ isAuthError: true });
    localStorage.removeItem('token');
    localStorage.removeItem('access_token');
    sessionStorage.clear();
    window.location.href = '/auth/login';
  }

  private handleLogoutBroadcast(): void {
    this.closeWarning(false);
    this.stop();
    // Clear local state and navigate using existing interceptor/guard behavior
    this.authTeardown.clearAuthentication({ isAuthError: true });
    localStorage.removeItem('token');
    localStorage.removeItem('access_token');
    sessionStorage.clear();
    window.location.href = '/auth/login';
  }

  private handleContinueSessionBroadcast(): void {
    this.closeWarning(false);
    this.tabActivityService.updateLastActivity();
  }

  private handleWarningStateBroadcast(isActive: boolean, endsAt: number): void {
    if (isActive && endsAt > 0 && endsAt <= Date.now()) {
      // Stale warning from a previous session/tab — clear, don't open modal
      this.tabActivityService.broadcastWarningState(false, 0);
      return;
    }

    if (isActive && endsAt > Date.now() && !this.warningModal) {
      this.warningEndsAt = endsAt;
      this.openWarning(false);
    } else if (!isActive && this.warningModal) {
      this.closeWarning(false);
    } else if (isActive && endsAt > Date.now()) {
      this.warningEndsAt = endsAt;
    }
  }
}
